import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import 'jspdf-autotable';
import { CSVLink } from 'react-csv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { Table, Select, Button, Modal, Form, Input, InputNumber, Checkbox, notification, Tabs, Row, Col, Space, Tag, Tooltip, DatePicker, Divider } from 'antd';
import { DeleteOutlined, EditOutlined, ReloadOutlined, ClearOutlined, InfoCircleOutlined } from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import InvoiceModal from './InvoiceModal';
import PaymentDetails from './PaymentDetails';
import { useTranslation } from 'react-i18next';
import './Accounting.css';
import moment from 'moment';
import dayjs from 'dayjs';

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

const formatDate = (dateString) => {
  return moment(dateString).format('DD MMM YYYY');
};

const formatNumber = (number) => {
  if (isNaN(number)) return '0.00';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const Accounting = () => {
  const { t } = useTranslation();
  const [registrations, setRegistrations] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [userBranchId, setUserBranchId] = useState(null);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [filters, setFilters] = useState({
  dateRange: {
    start: moment().startOf('month').format('YYYY-MM-DD'),
    end: moment().endOf('month').format('YYYY-MM-DD')
  },
  searchText: '',
  createdBy: '', // Add created_by filter
  hasRemaining: false, // Add remaining balance filter
});
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [password, setPassword] = useState('');
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
const [passwordError, setPasswordError] = useState('');
  const [form] = Form.useForm();

  // Edit-invoice-financials modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editLineItems, setEditLineItems] = useState([]); // [{ class_id, class_code, price }]
  const [editDiscount, setEditDiscount] = useState(0);
  const [editVat, setEditVat] = useState(false);
  const [editPassword, setEditPassword] = useState('');
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Get user info from token and pre-select branch
useEffect(() => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUserRole(payload.role);
      setUserBranchId(payload.branch_id || payload.branchId);

      // Auto-select branch for admin (not superadmin)
      if (payload.role !== 'superadmin') {
        setFilters(prev => ({
          ...prev,
          branchId: payload.branch_id || payload.branchId
        }));
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }
  }
}, []);


  // Fetch bank details
  const fetchBankDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBankDetails(response.data);
    } catch (error) {
      console.error('[fetchBankDetails] Error fetching bank details:', error);
      notification.error({
        message: t('error'),
        description: 'Failed to fetch bank details'
      });
    }
  }, [API_URL, t]);

const fetchRegistrations = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    // ✅ When searching, ignore the date range so results are global
    const isGlobalSearch = !!filters.searchText?.trim();

    let url = `${API_URL}/registrations`;
    const params = [];

    if (!isGlobalSearch && filters.dateRange?.start && filters.dateRange?.end) {
      const { start, end } = filters.dateRange;
      // ✅ Ensure dates are in YYYY-MM-DD format
      const startDate = new Date(start).toISOString().split('T')[0];
      const endDate = new Date(end).toISOString().split('T')[0];
      params.push(`dateFrom=${startDate}`, `dateTo=${endDate}`);
    }

    // ✅ Only superadmin can specify branchId manually
    if (userRole === 'superadmin' && filters.branchId) {
      params.push(`branchId=${filters.branchId}`);
    }

    if (params.length) {
      url += `?${params.join('&')}`;
    }

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // ⭐ Handle new response format with data wrapper
    const responseData = response.data.data || response.data;
    
    const processedData = responseData.map(reg => {
  // ✅ DEFINE IT HERE
  const totalCourseMinutes =
    reg.classes?.reduce(
      (sum, c) => sum + (c.course_hours || 0),
      0
    ) || 0;

  const totalScheduleMinutes =
    reg.classes?.reduce(
      (sum, c) => sum + (c.total_hours || 0),
      0
    ) || 0;

  return {
    ...reg,
    branch_name: reg.branch_name,
    branch_id: reg.branch_id,
    class_codes_display:
      reg.classes?.map(c => c.class_code).join(', ') || '',
    class_count: reg.classes?.length || 0,

    // Scheduled hours
    total_hours: totalScheduleMinutes / 60,

    // ✅ Course hours (from minutes)
    course_hours: totalCourseMinutes / 60,

    final_price: reg.batch_total_price,
    is_batch: reg.classes?.length > 1,
    id: reg.invoiceno,
    admin_display:
      reg.admin_nickname || `Admin ${reg.admin_id}` || 'Unknown'
  };
});


    setRegistrations(processedData);
    setFilteredRegistrations(processedData);
  } catch (error) {
    console.error('Error fetching registrations:', error);
  }
}, [API_URL, userRole, filters.dateRange, filters.branchId, filters.searchText]);


  // Fetch registrations (debounced so typing a search doesn't spam the API)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRegistrations();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchRegistrations]);

  // Fetch bank details on mount
  useEffect(() => {
    fetchBankDetails();
  }, [fetchBankDetails]);

  const handleDelete = (invoiceNo) => {
  setInvoiceToDelete(invoiceNo);
  setPasswordError('');
  setIsPasswordModalOpen(true);
};

  const confirmDelete = async (password) => {
  setIsPasswordModalOpen(false);
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      notification.error({
        message: t('error'),
        description: t('noTokenFound')
      });
      return;
    }

    await axios.delete(`${API_URL}/registrations/invoice/${invoiceToDelete}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { password },
    });

    // Remove from local state
    setRegistrations(prev => prev.filter(reg => reg.invoiceno !== invoiceToDelete));
    setFilteredRegistrations(prev => prev.filter(reg => reg.invoiceno !== invoiceToDelete));

    Modal.success({ 
      title: t('success'), 
      content: t('Registration batch deleted successfully') 
    });
  } catch (error) {
    console.error('Error deleting registration:', error);
    if (error.response && error.response.status === 401) {
      setPasswordError(t('invalidPassword'));
      setIsPasswordModalOpen(true);
    } else if (error.response?.data?.message === 'Cannot delete: Payments exist for this registration.') {
      notification.error({
        message: t('deleteError'),
        description: t('Cannot Delete Due To Payment(s): Payments exist for this registration.'),
      });

    } else if (error.response?.data?.message === 'Cannot delete: Attendance has been recorded for this registration.') {
      notification.error({
        message: t('deleteError'),
        description: t('Cannot Delete Due To Attendance: Attendances exist for this registration.'),
      });
    } else {
      notification.error({
        message: t('deleteError'),
        description: t('Cannot Delete This Item'),
      });
    }
  }
};

const handleOk = () => {
    if (!password.trim()) {
      setPasswordError(t('passwordRequired'));
      return;
    }
    confirmDelete(password);
    setPassword('');
  };

  const handleCancel = () => {
    setIsPasswordModalOpen(false);
    setPassword('');
    setPasswordError('');
  };

  // ----- Edit invoice financials -----
  const openEditModal = (record) => {
    // Pricing can only be edited on an unpaid invoice. If payments exist, the
    // user must delete them first (in the Payments tab).
    if ((Number(record.total_paid) || 0) > 0) {
      Modal.warning({
        title: t('Cannot edit pricing'),
        content: t('This invoice already has payment(s). Please delete the payment(s) in the Payments tab before editing the pricing.'),
      });
      return;
    }
    setEditingInvoice(record);
    setEditLineItems(
      (record.classes || []).map((c) => ({
        class_id: c.class_id,
        class_code: c.class_code,
        price: Number(c.price) || 0,
      }))
    );
    setEditDiscount(Number(record.discount) || 0);
    setEditVat(Number(record.vat) > 0);
    setEditPassword('');
    setEditError('');
    setIsEditModalOpen(true);
  };

  const handleEditLinePriceChange = (classId, value) => {
    setEditLineItems((prev) =>
      prev.map((li) => (li.class_id === classId ? { ...li, price: value } : li))
    );
  };

  const editSubtotal = editLineItems.reduce((sum, li) => sum + (Number(li.price) || 0), 0);
  const editBeforeVat = editSubtotal - (Number(editDiscount) || 0);
  const editVatAmount = editVat ? editBeforeVat * 0.07 : 0;
  const editTotal = editBeforeVat + editVatAmount;

  const submitEditFinancials = async () => {
    setEditError('');
    if ((Number(editDiscount) || 0) > editSubtotal) {
      setEditError(t('Discount cannot exceed the total price'));
      return;
    }
    if (!editPassword.trim()) {
      setEditError(t('passwordRequired'));
      return;
    }
    setEditSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/registrations/invoice/${editingInvoice.invoiceno}/financials`,
        {
          password: editPassword,
          lineItems: editLineItems.map((li) => ({
            class_id: li.class_id,
            price: Number(li.price) || 0,
          })),
          discount: Number(editDiscount) || 0,
          vatEnabled: editVat,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsEditModalOpen(false);
      notification.success({
        message: t('success'),
        description: t('Invoice updated successfully'),
      });
      fetchRegistrations();
    } catch (error) {
      console.error('Error updating invoice financials:', error);
      if (error.response?.status === 401) {
        setEditError(t('invalidPassword'));
      } else {
        setEditError(error.response?.data?.message || t('updateError'));
      }
    } finally {
      setEditSubmitting(false);
    }
  };

const applyFilters = useCallback(() => {
  let filtered = registrations;

  // Date range filter (skipped during a global search)
  if (!filters.searchText?.trim() && filters.dateRange?.start && filters.dateRange?.end) {
    const { start, end } = filters.dateRange;
    const startDate = moment(start);
    const endDate = moment(end);

    filtered = filtered.filter(registration => {
      const regDate = moment(registration.date);
      return regDate.isBetween(startDate, endDate, 'day', '[]');
    });
  }

  // Search text filter
  if (filters.searchText) {
    const searchLower = filters.searchText.toLowerCase();
    filtered = filtered.filter(registration => 
      registration.invoiceno.toLowerCase().includes(searchLower) ||
      registration.student_name.toLowerCase().includes(searchLower) ||
      registration.class_codes_display.toLowerCase().includes(searchLower)
    );
  }

  // Created by filter
  if (filters.createdBy) {
    filtered = filtered.filter(registration => 
      registration.admin_display.toLowerCase().includes(filters.createdBy.toLowerCase())
    );
  }

  // Remaining balance filter
  if (filters.hasRemaining) {
    filtered = filtered.filter(registration => 
      registration.remaining_balance > 0
    );
  }

  // Branch filter
  if (filters.branchId) {
    filtered = filtered.filter(registration =>
      String(registration.branch_id) === String(filters.branchId)
    );
  }

  setFilteredRegistrations(filtered);
}, [registrations, filters]);


// Add this function after the calculateTotals function
const getUniqueAdmins = () => {
  const adminSet = new Set();
  registrations.forEach(reg => {
    if (reg.admin_display && reg.admin_display !== 'Unknown') {
      adminSet.add(reg.admin_display);
    }
  });
  return Array.from(adminSet).sort();
};

  // Apply filters whenever filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

const clearFilters = () => {
  setFilters({
    searchText: '',
    createdBy: '',
    hasRemaining: false,
    dateRange: {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().endOf('month').format('YYYY-MM-DD'),
    },
  });
};

  const handlePaymentMethodChange = (value) => {
    setSelectedPaymentMethod(value);
    
    // Clear bank account selection if not bank transfer
    if (value !== 'bank_transfer') {
      form.setFieldsValue({ bank_account_id: undefined });
    } else {
      // Auto-select default bank for bank transfers
      if (bankDetails.length > 0) {
        const defaultBank = bankDetails.find(bank => bank.is_default) || bankDetails[0];
        form.setFieldsValue({ bank_account_id: defaultBank.id });
      }
    }
  };

 const handleModalOk = async () => {
  try {
    const values = form.getFieldsValue();

    const paymentAmount = parseFloat(values.payment_amount);
    const remainingAmount = selectedRegistration.final_price - paymentAmount;

    const paymentData = {
      invoiceno: selectedRegistration.invoiceno,
      student_id: selectedRegistration.student_id,
      batch_id: selectedRegistration.batch_id, // ✅ Include batch_id
      pay_amount: paymentAmount,
      remaining_balance: Math.round(remainingAmount),
      payment_method: values.payment_method,
      memo: values.memo,
      payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD') : null,
      manual_receipt_no: values.manual_receipt_no ? values.manual_receipt_no.trim() : null,
    };

    // Only include bank_account_id for bank transfers
    if (values.payment_method === 'bank_transfer') {
      if (!values.bank_account_id) {
        notification.error({
          message: t('error'),
          description: t('Please select a bank account for bank transfer'),
        });
        return;
      }
      paymentData.bank_account_id = values.bank_account_id;
    }

    const token = localStorage.getItem('token');

    await axios.post(`${API_URL}/payment`, paymentData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    notification.success({
      message: t('paymentConfirmed'),
      description: t('Payment has been confirmed!'),
    });

    setIsPaymentModalOpen(false);
    setSelectedPaymentMethod('');
    form.resetFields();
    fetchRegistrations();
  } catch (error) {
    console.error('Error in handleModalOk:', error);

    if (error.response) {
      console.error('API Error Response:', error.response.data);
    }

    notification.error({
      message: t('error'),
      description: t('payment_log_error'),
    });
  }
};


  const openPaymentModal = (registration) => {
    setSelectedRegistration(registration);
    setSelectedPaymentMethod('');
    form.resetFields();
    setIsPaymentModalOpen(true);
  };
  
  const openInvoiceModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsInvoiceModalOpen(true);
  };
  
  const handleInvoiceModalClose = () => {
    setIsInvoiceModalOpen(false);
    setSelectedTransaction(null);
  };
  
  const calculateTotals = () => {
    return filteredRegistrations.reduce(
      (totals, registration) => {
        totals.classPrice += parseFloat(registration.class_price) || 0;
        totals.discount += parseFloat(registration.discount) || 0;
        totals.vat += parseFloat(registration.vat) || 0;
        totals.finalPrice += parseFloat(registration.batch_total_price) || 0;
        return totals;
      },
      { classPrice: 0, discount: 0, vat: 0, finalPrice: 0 }
    );
  };

  const totals = calculateTotals();
  
  // Enhanced CSV data for new structure
// Enhanced CSV data for new structure
const csvData = [
  ...filteredRegistrations.map(reg => ({
    'BRANCH': reg.branch_name || `Branch ${reg.branch_id}`,
    'INVOICE NO.': reg.invoiceno.replace('INV', ''),
    'MANUAL RECEIPT NO.': reg.manual_receipt_nos || '',
    'DATE': moment(reg.date).format('DD MMM YYYY'),
    'STUDENT': reg.student_name,
    'CLASSES': reg.class_codes_display,
    'CLASS COUNT': reg.class_count,
    'CLASS PRICE': reg.class_price,
    'DISCOUNT': reg.discount || 0,
    'VAT': reg.vat || 0,
    'TOTAL': reg.batch_total_price,
    'REMAINING': reg.remaining_balance > 0 ? `(${reg.remaining_balance})` : 0,
    'CREATED BY': reg.admin_display,   // Admin info
    
  })),
  {
    'BRANCH': '',
    'INVOICE NO.': 'TOTAL',
    'MANUAL RECEIPT NO.': '',
    'DATE': '',
    'STUDENT': '',
    'CLASSES': '',
    'CLASS COUNT': '',
    'CLASS PRICE': totals.classPrice,
    'DISCOUNT': totals.discount,
    'VAT': totals.vat,
    'TOTAL': totals.finalPrice,
    'REMAINING': filteredRegistrations.reduce(
      (sum, reg) => sum + (parseFloat(reg.remaining_balance) || 0), 
      0
    ),
    'CREATED BY': ''

  }
];



    const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

  const columns = [
    {
  title: t('invoiceNo'),
  dataIndex: 'invoiceno',
  key: 'invoiceno',
  render: (text, record) => (
    <Button type="link" onClick={() => openInvoiceModal(record)}>
      {text}
    </Button>
  ),
},
{
  title: t('branch'),
  dataIndex: 'branch_name',
  key: 'branch_name',
  width: 150,
  render: (text, record) => (
    <Tag color={branchColors[record.branch_id] || "default"}>
      {text || t("N/A")}
    </Tag>
  ),
},
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: formatDate,
    },
    {
      title: t('student'),
      dataIndex: 'student_name',
      key: 'student_name',
    },
{
  title: t('Classes'),
  key: 'classes',
  width: 200,
  render: (_, record) => (
    <div style={{
          fontWeight: 'bold',
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          width: '150px',
        }}>
      <div style={{ fontWeight: 'bold' }}>
        {record.class_codes_display}
      </div>
      <div style={{ fontSize: '12px', color: '#666' }}>
        <Tag size="small" color={record.is_batch ? 'blue' : 'green'}>
          {record.class_count} {record.class_count === 1 ? 'class' : 'classes'}
        </Tag>
        <span style={{ marginLeft: 8 }}>
          {record.course_hours.toFixed(1)} {t('hours')}
        </span>
      </div>
    </div>
  ),
},
    {
  title: t('Pricing'),
  key: 'pricing',
  render: (_, record) => (
    <div style={{ fontSize: '12px' }}>
      <div>
        <span style={{ color: '#666' }}>{t('Class Price')}: </span>
        {formatNumber(record.class_price)}
      </div>
      <div>
        <span style={{ color: '#666' }}>{t('discount')}: </span>
        {formatNumber(record.discount)}
      </div>
      <div>
        <span style={{ color: '#666' }}>{t('VAT')}: </span>
        {formatNumber(record.vat)}
      </div>
    </div>
  ),
},
    {
      title: t('total'),
      dataIndex: 'batch_total_price',
      key: 'batch_total_price',
      align: 'right',
      render: (value) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {formatNumber(value)}
        </span>
      ),
    },
    {
      title: t('remaining'),
      dataIndex: 'remaining_balance',
      key: 'remaining_balance',
      render: (balance) =>
        balance > 0 ? (
          <span style={{ fontWeight: 'bold', color: 'red' }}>({formatNumber(Math.abs(balance))})</span>
        ) : (
          '0'
        ),
    },
    {
      title: t('pay'),
      key: 'pay',
      render: (_, record) => {
        const isDisabled = record.remaining_balance <= 0;
        return (
          <Button 
            onClick={() => openPaymentModal(record)} 
            icon={<FontAwesomeIcon icon={faDollarSign} />}
            disabled={isDisabled}
            style={{ opacity: isDisabled ? 0.5 : 1 }}
          />
        );
      },
    },
    {
      title: t('By'),
      dataIndex: 'admin_display',
      key: 'admin_display',
      render: (admin) => (
        <Tag color="blue" size="small">
          {admin}
        </Tag>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('Edit invoice pricing')}>
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={t('delete')}>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.invoiceno)}
            />
          </Tooltip>
        </Space>
      ),
    },

  ];

  return (
    <div className="accounting-container">
      <Sidebar />
      <Header />
      <h1>{t('accounting')}</h1>

      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: "1",
            label: t('INVOICES'),
            children: (
              <>
               <Row gutter={[8, 8]} align="middle" wrap={true} style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                  {/* Date Range */}
                  <Col>
                    <Row gutter={[8, 8]} align="middle">
                                         {/* Branch Filter */}
                     {userRole === 'superadmin' && (
                          <Col>
                            <Select
                              value={filters.branchId || ''}
                              onChange={(value) => handleFilterChange('branchId', value || null)}
                              placeholder={t("Filter by branch")}
                              style={{ width: 180 }}
                              allowClear
                            >
                              <Option value="">{t("All Branches")}</Option>
                              {[...new Set(registrations.map(reg => reg.branch_id))].map(branchId => {
                                const branchName = registrations.find(reg => reg.branch_id === branchId)?.branch_name || `Branch ${branchId}`;
                                return (
                                  <Option key={branchId} value={branchId}>
                                    {branchName}
                                  </Option>
                                );
                              })}
                            </Select>
                          </Col>
                        )}

                      <Col>
                        <RangePicker
                          format="DD MMM YYYY"
                          value={[
                            filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
                            filters.dateRange.end ? dayjs(filters.dateRange.end) : null,
                          ]}
                          onChange={(dates) =>
                            handleFilterChange('dateRange', {
                              start: dates?.[0] ? dates[0].format('YYYY-MM-DD') : null,
                              end: dates?.[1] ? dates[1].format('YYYY-MM-DD') : null,
                            })
                          }
                          style={{ height: 32 }}
                          allowClear
                        />
                      </Col>
                    </Row>
                    </Col>

                  {/* Search */}
                  <Col>
                    <Search
                      placeholder={t('Search by invoice, student name, or classes')}
                      value={filters.searchText}
                      onChange={(e) => handleFilterChange('searchText', e.target.value)}
                      style={{ width: 250 }}
                      allowClear
                    />
                  </Col>
   

                  {/* Admin Filter */}
                  <Col>
                    <Select
                      placeholder={t('Filter by admin')}
                      value={filters.createdBy || undefined}
                      onChange={(value) => handleFilterChange('createdBy', value || '')}
                      allowClear
                      style={{ width: 200 }}
                    >
                      {getUniqueAdmins().map((admin) => (
                        <Option key={admin} value={admin}>
                          {admin}
                        </Option>
                      ))}
                    </Select>
                  </Col>

                  {/* Has Remaining Checkbox */}
                  <Col>
                        <Select
                          value={filters.hasRemaining !== null ? filters.hasRemaining.toString() : ''}
                          onChange={(value) => {
                            const val = value === '' ? null : value === 'true';
                            handleFilterChange('hasRemaining', val);
                          }}
                          placeholder="Balance filter"
                          style={{ width: 180 }}
                          allowClear
                        >
                          <Option value="true">{t('Has remaining balance')}</Option>
                          <Option value="false">{t('Overdue')}</Option>
                        </Select>
                  </Col>

                  {/* Buttons */}
                  <Col>
                    <Space>
                      <Button onClick={clearFilters} icon={<ClearOutlined />}>
                        {t('Clear Filter')}
                      </Button>
                      <Button onClick={fetchRegistrations} icon={<ReloadOutlined />}>
                        {t('refresh')}
                      </Button>
                          <CSVLink
                          data={csvData}
                          filename={`registrations_${moment().format('DD-MMM-YYYY')}.csv`}
                          target="_blank"
                          headers={[
                                { label: 'BRANCH', key: 'BRANCH' },
                            { label: 'INVOICE NO.', key: 'INVOICE NO.' },
                            { label: 'MANUAL RECEIPT NO.', key: 'MANUAL RECEIPT NO.' },
                            { label: 'DATE', key: 'DATE' },
                            { label: 'STUDENT', key: 'STUDENT' },
                            { label: 'CLASSES', key: 'CLASSES' },
                            { label: 'CLASS COUNT', key: 'CLASS COUNT' },
                            { label: 'CLASS PRICE', key: 'CLASS PRICE' },
                            { label: 'DISCOUNT', key: 'DISCOUNT' },
                            { label: 'VAT', key: 'VAT' },
                            { label: 'TOTAL', key: 'TOTAL' },
                            { label: 'REMAINING', key: 'REMAINING' },
                            { label: 'CREATED BY', key: 'CREATED BY' }

                          ]}
                        >
                          <Button icon={<FontAwesomeIcon icon={faFileExcel} />}>
                            {t('exportCSV')}
                          </Button>
                        </CSVLink>

                    </Space>
                  </Col>
                </Row>
                <div className="scrollable-table">
                  <Table
                    dataSource={filteredRegistrations}
                    columns={columns}
                    scroll={{ x: 'max-content' }}
                    sticky
                    pagination={{
                      pageSize: 50,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    rowKey="invoiceno"
                  />
                </div>
              </>
            ),
          },
          {
            key: "2",
            label: t('PAYMENTS'),
            children: <PaymentDetails />,
          },
        ]}
      />

      {/* Invoice Modal */}
      {selectedTransaction && (
        <InvoiceModal
          visible={isInvoiceModalOpen}
          onClose={handleInvoiceModalClose}
          registration={selectedTransaction}
          API_URL={API_URL}
        />
      )}

      {/* Enhanced Payment Modal */}
      <Modal
        title={t('makePayment')}
        open={isPaymentModalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setIsPaymentModalOpen(false);
          setSelectedPaymentMethod('');
          form.resetFields();
        }}
        width={600}
      >
        {selectedRegistration && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <strong>{t('Invoice')}:</strong> {selectedRegistration.invoiceno}
              </Col>
              <Col span={12}>
                <strong>{t('Student')}:</strong> {selectedRegistration.student_name}
              </Col>
              <Col span={12}>
                <strong>{t('Classes')}:</strong> {selectedRegistration.class_codes_display}
              </Col>
              <Col span={12}>
                <strong>{t('Class Count')}:</strong> {selectedRegistration.class_count}
              </Col>
              <Col span={12}>
                <strong>{t('Total Amount')}:</strong> ฿{formatNumber(selectedRegistration.batch_total_price)}
              </Col>
              <Col span={12}>
                <strong style={{ color: 'red' }}>{t('Remaining')}:</strong> ฿{formatNumber(selectedRegistration.remaining_balance)}
              </Col>
            </Row>
          </div>
        )}
        
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="payment_amount" 
                label={t('paymentAmount')} 
                rules={[
                  { required: true, message: t('Please enter payment amount') },
                  { 
                    validator: (_, value) => {
                      if (value && parseFloat(value) > selectedRegistration?.remaining_balance) {
                        return Promise.reject(new Error(t('Payment amount cannot exceed remaining balance')));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input 
                  placeholder={`Max: ${selectedRegistration?.remaining_balance || 0}`}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="payment_method"
                label={t('paymentMethod')}
                rules={[{ required: true, message: t('Please select payment method') }]}
              >
                <Select
                  placeholder={t('Select payment method')}
                  onChange={handlePaymentMethodChange}
                >
                  <Option value="cash">{t('Cash')}</Option>
                  <Option value="credit_card">{t('Credit Card')}</Option>
                  <Option value="bank_transfer">{t('Bank Transfer')}</Option>
                  <Option value="online_payment">{t('Online Payment')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="payment_date"
                label={t('Payment Date')}
                initialValue={dayjs()}
              >
                <DatePicker
                  format="DD MMM YYYY"
                  style={{ width: '100%' }}
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="manual_receipt_no"
                label={t('Your Receipt Number (optional)')}
                tooltip={t('Leave blank to use the auto-generated receipt number')}
              >
                <Input placeholder={t('e.g. your custom receipt no.')} />
              </Form.Item>
            </Col>
          </Row>

          {/* Conditionally render bank account selection only for bank transfers */}
          {selectedPaymentMethod === 'bank_transfer' && (
            <Form.Item 
              name="bank_account_id" 
              label={t('Receiving Bank Account')} 
              rules={[{ required: true, message: t('Please select bank account') }]}
            >
              <Select 
                placeholder={t('Select bank account')}
                loading={bankDetails.length === 0}
              >
                {bankDetails.map(bank => (
                  <Option key={bank.id} value={bank.id}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {bank.bank_name} - {bank.account_number} ({bank.account_name})
                        {bank.is_default && <span style={{ color: '#52c41a', fontSize: '12px' }}> (Default)</span>}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          
          <Form.Item name="memo" label={t('memo')}>
            <Input.TextArea 
              rows={3}
              placeholder={t('Optional payment notes or reference')}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
      title={t('Confirm Deletion')}
      open={isPasswordModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={t('Delete')}
      cancelText={t('Cancel')}
      okType="danger"
    >
      <div style={{ marginBottom: 16 }}>
        <p><strong>{t('Invoice')}:</strong> {invoiceToDelete}</p>
        <p style={{ color: '#ff4d4f' }}>
          {t('This action cannot be undone!')}
        </p>
      </div>
      
      <div>
        <p>{t('Please enter your password to confirm deletion')}:</p>
        <Input.Password
          placeholder={t('Enter your password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={handleOk}
          status={passwordError ? 'error' : ''}
        />
        {passwordError && (
          <div style={{ color: 'red', marginTop: 8 }}>
            {passwordError}
          </div>
        )}
      </div>
    </Modal>

    {/* Edit Invoice Financials Modal */}
    <Modal
      title={t('Edit Invoice Pricing')}
      open={isEditModalOpen}
      onOk={submitEditFinancials}
      onCancel={() => setIsEditModalOpen(false)}
      okText={t('Save Changes')}
      cancelText={t('Cancel')}
      confirmLoading={editSubmitting}
      width={640}
    >
      {editingInvoice && (
        <>
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}><strong>{t('Invoice')}:</strong> {editingInvoice.invoiceno}</Col>
              <Col span={12}><strong>{t('Student')}:</strong> {editingInvoice.student_name}</Col>
            </Row>
          </div>

          {/* Per-line prices */}
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('Line Items')}</div>
          {editLineItems.map((li) => (
            <Row key={li.class_id} gutter={16} align="middle" style={{ marginBottom: 8 }}>
              <Col span={14}>
                <Tag color="blue">{li.class_code}</Tag>
              </Col>
              <Col span={10}>
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  prefix="฿"
                  style={{ width: '100%' }}
                  value={li.price}
                  onChange={(value) => handleEditLinePriceChange(li.class_id, value)}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/฿\s?|(,*)/g, '')}
                />
              </Col>
            </Row>
          ))}

          <Divider style={{ margin: '12px 0' }} />

          <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
            <Col span={14}>{t('discount')} (฿)</Col>
            <Col span={10}>
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                prefix="฿"
                style={{ width: '100%' }}
                value={editDiscount}
                onChange={(value) => setEditDiscount(value || 0)}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/฿\s?|(,*)/g, '')}
              />
            </Col>
          </Row>

          <Row style={{ marginBottom: 12 }}>
            <Col span={24}>
              <Checkbox checked={editVat} onChange={(e) => setEditVat(e.target.checked)}>
                {t('Apply VAT')} (7%)
              </Checkbox>
            </Col>
          </Row>

          {/* Computed totals */}
          <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
            <Row justify="space-between"><span>{t('subtotal')}</span><span>฿{formatNumber(editSubtotal)}</span></Row>
            <Row justify="space-between"><span>{t('discount')}</span><span style={{ color: '#d32f2f' }}>- ฿{formatNumber(Number(editDiscount) || 0)}</span></Row>
            {editVat && (
              <Row justify="space-between"><span>{t('VAT')} (7%)</span><span>฿{formatNumber(editVatAmount)}</span></Row>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <Row justify="space-between" style={{ fontWeight: 700, fontSize: 16 }}>
              <span>{t('total')}</span>
              <span style={{ color: '#1890ff' }}>฿{formatNumber(editTotal)}</span>
            </Row>
          </div>

          <div style={{ marginTop: 16 }}>
            <p style={{ marginBottom: 4 }}>{t('Please enter your password to confirm')}:</p>
            <Input.Password
              placeholder={t('Enter your password')}
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              onPressEnter={submitEditFinancials}
              status={editError ? 'error' : ''}
            />
            {editError && <div style={{ color: 'red', marginTop: 8 }}>{editError}</div>}
          </div>
        </>
      )}
    </Modal>
    </div>
  );
};

export default withRoleAccess(Accounting, ['superadmin', 'admin']);